import { BuilderConfig, FontAwesomeIconKey } from './builderConfigs'
import { useSelector } from '../..'
import { Spin, Alert } from 'antd'

const MAX_TITLE_LENGTH = 100
const MAX_DESC_LENGTH = 500

const icon: FontAwesomeIconKey = 'fa-check-square-o'

export const QuizBuilderConfig: BuilderConfig<'quiz'> = {
  key: 'quiz',
  builderHomeData: {
    type: 'quiz',
    description: (
      <p>
        Upload a JSON file defining a multiple-choice quiz — questions, answers,
        pass threshold, and navigation — for interactive use on web and mobile.
      </p>
    ),
    enabled: true,
    icon: 'fa-check-square-o',
    route: '/builders/quiz',
    title: 'Quiz',
  },
  previewLabels: {
    title: 'Quiz',
    description:
      'A Quiz asset is an interactive multiple-choice assessment configured ' +
      'from a JSON file and rendered natively on web and mobile.',
  },
  crumbTrail: {
    linkTo: '/builders/home',
    crumbText: 'Quiz',
    crumbIcon: `fa ${icon}`,
  },
  fieldValidators: {
    title: {
      validateTrigger: 'onBlur',
      rules: [
        { required: true, message: 'Title is required' },
        {
          max: MAX_TITLE_LENGTH,
          message: `Title has a maximum character limit of ${MAX_TITLE_LENGTH}`,
        },
      ],
    },
    description: {
      validateTrigger: 'onBlur',
      rules: [
        {
          max: MAX_DESC_LENGTH,
          message: `Description has a maximum character limit of ${MAX_DESC_LENGTH}`,
        },
      ],
    },
  },
  formLabels: {},
  icon,
  screenHandlers: {
    createScreen: {
      next: (saveToDb) => saveToDb(),
    },
    metaScreen: {
      next: (saveToDb) => saveToDb(),
    },
  },
  assetSerializer: (data) => {
    const { builderData, user } = data
    const assetMetaData = {
      id: builderData.id,
      asset_type: QuizBuilderConfig.key,
      owner: user.id,
      title: builderData.title,
      description: builderData.description,
      tags: builderData.tags,
      filename: builderData.quizFileName,
      icon,
      color: '#00008b',
      preview: null,
    }
    const assetData = {
      quizConfig: builderData.quizConfig,
      image: builderData.leadImage,
    }
    return {
      assetMetaData,
      assetData,
      ContentType: 'application/json',
      assetDataSerialized: JSON.stringify(assetData),
    }
  },
  Content: () => {
    const quizState = useSelector((state: any) => state.builders.quiz)

    if (quizState.jsonUploading) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Spin tip="Validating quiz config..." />
        </div>
      )
    }

    if (quizState.jsonValid && quizState.quizConfig) {
      const q = quizState.quizConfig
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 16,
          }}
        >
          <i
            className="fa fa-check-circle"
            style={{ fontSize: 48, color: '#52c41a' }}
          />
          <p>
            <strong>{quizState.quizFileName}</strong> validated
          </p>
          <p style={{ color: '#888', fontSize: 13 }}>
            {q.questions ? q.questions.length : 0} question
            {q.questions && q.questions.length !== 1 ? 's' : ''} &middot; Pass
            threshold: {q.passThreshold}%
          </p>
        </div>
      )
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 16,
        }}
      >
        {quizState.jsonError && (
          <Alert message={quizState.jsonError} type="error" showIcon />
        )}
        <p>Upload a quiz JSON file using the form on the right.</p>
      </div>
    )
  },
}
